import React, { FormEvent, useContext } from 'react';
import { useAsyncFn } from 'react-use';
import { useMutation, useApolloClient } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import { Alert, Button, Text, Notification, Input } from '@perch-labs/shrike';
import { useAsyncSetState } from 'hooks/use-async-set-state.hook';
import { useUser } from 'hooks/use-user.hook';
import { copyFor, routes } from 'config';
import { LoadingPage } from 'components/loading';
import { ErrorPage } from 'components/errors';
import { Page } from 'styles';
import { BackLink } from 'components/back-link';
import { NOTIFICATION_DISPLAY_TIME } from 'domain/ux';
import { MAX_SECONDARY_PAGE_WIDTH } from 'domain/style';
import { EmailTakenError } from 'utils/errors/client-errors';
import { translateError } from 'utils/errors/translate-error';
import {
  EMAIL_EXISTS_QUERY,
  UPDATE_USER_MUTATION,
} from '../personal-information.api';
import { validateForm } from './edit-login.utils';

const getCopy = copyFor('settings.editLogin');
const { personalInformation } = routes();

export const EditLogin = () => {
  const { addNotification } = useContext(Notification.Context);
  const client = useApolloClient();
  const history = useHistory();
  const { data: user, loading: userLoading, error: userError } = useUser();
  const [mutate] = useMutation(UPDATE_USER_MUTATION);
  const [variables, setVariables] = useAsyncSetState(
    user,
    (stateUser: typeof user) => ({
      currentEmail: stateUser?.email,
      currentPassword: '',
      email: stateUser?.email,
      password: '',
    })
  );
  // @ts-expect-error ts-migrate(2339) FIXME: Property 'currentEmail' does not exist on type '{}... Remove this comment to see the full error message
  const { currentEmail, email, password } = variables;
  const emailChanged = email !== currentEmail;
  const valuesChanged =
    emailChanged ||
    (variables as any).password !== (variables as any).currentPassword;

  const [{ loading, error }, submit] = useAsyncFn(async () => {
    const input = { email, password, skipCurrentPasswordConfirmation: true };
    if (emailChanged) {
      const { data } = await client.query({
        query: EMAIL_EXISTS_QUERY,
        variables: { email },
      });
      if (data.userExists) {
        throw new EmailTakenError();
      }
    }
    await mutate({
      variables: {
        id: user?.id,
        input,
      },
    });
    addNotification({
      displayTime: NOTIFICATION_DISPLAY_TIME,
      message: getCopy('updateNotification'),
      variant: 'success',
    });
    history.push(personalInformation);
  }, [email, password, emailChanged, client, mutate, addNotification, history]);

  if (userLoading) return <LoadingPage numberOfSections={1} />;
  if (userError || !user)
    return <ErrorPage to={personalInformation} title={getCopy('title')} />;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (validateForm({ setVariables, variables })) submit();
  };
  return (
    <>
      <BackLink to={personalInformation} />
      <Page maxWidth={MAX_SECONDARY_PAGE_WIDTH}>
        <Text textStyle="heading800" tag="h2" margin={{ bottom: '48px' }}>
          {getCopy('title')}
        </Text>
        <form onSubmit={handleSubmit}>
          <Input
            errorText={(variables as any).emailError}
            label={getCopy('labels.email')}
            margin={{ bottom: '16px' }}
            name={getCopy('labels.email')}
            onChange={(value: string) =>
              setVariables({ email: value, emailError: null })
            }
            value={(variables as any).email}
          />
          <Input
            errorText={(variables as any).passwordError}
            label={getCopy('labels.password')}
            margin={{ bottom: '16px' }}
            name={getCopy('labels.password')}
            onChange={(value: string) =>
              setVariables({ password: value, passwordError: null })
            }
            type="password"
            value={(variables as any).password}
          />
          <Alert margin={{ top: '32px' }}>{translateError(error)}</Alert>
          <Button
            disabled={
              !valuesChanged ||
              !(variables as any).password ||
              !(variables as any).email
            }
            fullWidth
            loading={loading}
            margin={{ top: '32px' }}
            type="submit"
          >
            {getCopy('buttons.update')}
          </Button>
        </form>
      </Page>
    </>
  );
};
